import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { authRequestErrorMessage } from "../auth/authRequest";
import { toast } from "sonner";
import { CreditCard, Lock, Sparkles, CheckCircle2, AlertTriangle, Tag, X, MapPin, ArrowLeftRight } from "lucide-react";
import SplitDateTime from "../components/SplitDateTime";
import KycModal from "../components/KycModal";
import PolicyCard from "../components/PolicyCard";
import TelematicsPanel from "../components/TelematicsPanel";
import LateReturnWarning from "../components/LateReturnWarning";
import TravelPackToggle from "../components/TravelPackToggle";
import EsimCard from "../components/EsimCard";
import { pageItems } from "../lib/pageItems";
import { parseDateTimeLocal, rangeOverlapsRanges, rangeHitsHostCalendar, rangeHitsScheduledPause, billableDays, bookingWindowError, nextTimeSlotFromNow, syncReturnAfterPickupChange, extractTime, defaultReturnForPickup, clientTimeZone, minPickupWithLeadHours, pickupLeadTimeError, vehicleDeliveryLeadHours, formatTripRange, bookingTimeZoneLabel, formatLocalTimestamp } from "../lib/dateLocal";
import { formatMoney, formatNumber, vehicleCurrency } from "../lib/currencyDisplay";
import { useBookingDraft } from "../lib/useBookingDraft";
import { useTripQuote } from "../lib/useTripQuote";
import { formatBookingError, ONE_WAY_MAX_KM } from "../lib/bookingErrors";
import { formatBookingStatus, bookingStatusDotClass } from "../lib/bookingStatus";
import { getDayBreakdown } from "../lib/tripPricingDisplay";
import { bookingQuoteDisplay, canPayWithTripQuote } from "../lib/bookingQuoteDisplay";
import TripChargesGroup from "../components/TripChargesGroup";
import ListingImage from "../components/ListingImage";
import BookingScheduleSummary from "../components/BookingScheduleSummary";
import { formatIncludedDistanceLine } from "../lib/includedDistance";
import { ROAD_RULES, formatRoadRuleLine } from "../lib/roadRules";

export default function Booking() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [v, setV] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [redeemZct, setRedeemZct] = useState(0);
  const [applyWallet, setApplyWallet] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [kycOpen, setKycOpen] = useState(false);
  const [bookedRanges, setBookedRanges] = useState([]);
  const [calendarMode, setCalendarMode] = useState("block_unavailable");
  const [calendarDays, setCalendarDays] = useState([]);
  const [listingPauses, setListingPauses] = useState([]);
  const [coupon, setCoupon] = useState(null);          // applied coupon { code, amount_usd }
  const [couponInput, setCouponInput] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [pickupLoc, setPickupLoc] = useState("");
  const [oneWay, setOneWay] = useState(false);
  const [dropoffLoc, setDropoffLoc] = useState("");
  const [dropoffPostal, setDropoffPostal] = useState("");
  const [addEsim, setAddEsim] = useState(true);    // Travel-pack opt-in (free, defaults on)
  const [airaloReady, setAiraloReady] = useState(null);
  const [confirmedEsim, setConfirmedEsim] = useState(null);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [activeBooking, setActiveBooking] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const { saveDraft, clearDraft } = useBookingDraft();

  const start = params.get("start");
  const end = params.get("end");
  const prevStartRef = useRef(start);
  const [minPickup, setMinPickup] = useState(() => nextTimeSlotFromNow());

  const refreshMinPickup = () => setMinPickup(minPickupWithLeadHours(vehicleDeliveryLeadHours(v)));

  const setDate = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: true });
  };

  const onStartChange = (val) => {
    const prev = prevStartRef.current;
    prevStartRef.current = val;
    setDate("start", val);
    if (!val) return;
    const aligned = syncReturnAfterPickupChange(val, end, prev);
    if (aligned && aligned !== end) setDate("end", aligned);
  };

  // Fix stale URL params only when pick-up date changes (not time-only edits).
  useEffect(() => {
    if (!start) return;
    const prev = prevStartRef.current;
    if (prev && prev.split("T")[0] === start.split("T")[0]) return;
    prevStartRef.current = start;
  }, [start]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadError(null);
      try {
        const [vr, lr, ar, cr] = await Promise.all([
          axios.get(`${API}/vehicles/${id}`),
          axios.get(`${API}/loyalty/me`, { withCredentials: true }),
          axios.get(`${API}/vehicles/${id}/availability`),
          axios.get(`${API}/coupons/me`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setV(vr.data);
        setLoyalty(lr.data);
        setBookedRanges(ar.data?.booked_ranges || []);
        setCalendarMode(ar.data?.calendar_mode || "block_unavailable");
        setCalendarDays(ar.data?.calendar_days || ar.data?.blocked_days || []);
        setListingPauses(ar.data?.listing_pauses || []);
        setActiveCoupons(pageItems(cr.data));
        setMinPickup(minPickupWithLeadHours(vehicleDeliveryLeadHours(vr.data)));
        // Prefill pickup with the vehicle's listed address (or city as a fallback).
        setPickupLoc((vr.data?.location_address || vr.data?.location_city || "").trim());
      } catch (e) {
        if (cancelled) return;
        const msg = authRequestErrorMessage(e);
        setLoadError(msg);
        toast.error("Could not load checkout");
      }
    })();
    return () => { cancelled = true; };
  }, [id, loadAttempt]);

  useEffect(() => {
    if (!user) {
      setActiveBooking(null);
      return;
    }
    axios.get(`${API}/bookings/mine/active`, { withCredentials: true })
      .then((r) => setActiveBooking(r.data || null))
      .catch(() => setActiveBooking(null));
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/esim/airalo/status`)
      .then((r) => {
        const ok = !!r.data?.configured;
        setAiraloReady(ok);
        if (!ok) setAddEsim(false);
      })
      .catch(() => { setAiraloReady(false); setAddEsim(false); });
  }, []);

  useEffect(() => {
    axios.get(`${API}/payments/stripe/config`)
      .then((r) => setStripeConfigured(!!r.data?.configured))
      .catch(() => setStripeConfigured(false));
  }, []);

  // Stripe success URL should be /book/:id/complete — redirect if we only got ?session_id= on checkout.
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId || params.get("stripe_cancel") === "1") return;
    navigate(`/book/${id}/complete?session_id=${encodeURIComponent(sessionId)}`, { replace: true });
  }, [id, params, navigate]);

  // Release reserved dates when user cancels Stripe Checkout.
  useEffect(() => {
    if (params.get("stripe_cancel") !== "1") return;
    const sessionId = params.get("session_id");
    if (sessionId) {
      axios.post(`${API}/payments/stripe/cancel`, { session_id: sessionId }, { withCredentials: true })
        .catch(() => {});
    }
    toast.message("Payment cancelled — you can try Stripe Checkout again");
    const next = new URLSearchParams(params);
    next.delete("stripe_cancel");
    next.delete("session_id");
    setParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyCoupon = async (codeOpt) => {
    const code = (codeOpt ?? couponInput).trim().toUpperCase();
    if (!code) return;
    setCouponBusy(true);
    try {
      const r = await axios.post(`${API}/coupons/validate`, { code }, { withCredentials: true });
      setCoupon({ code: r.data.code, amount_usd: r.data.amount_usd });
      setCouponInput("");
      toast.success(`Code applied — $${r.data.amount_usd} off`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid or expired code");
    } finally { setCouponBusy(false); }
  };

  const removeCoupon = () => { setCoupon(null); };

  const days = useMemo(() => billableDays(start, end), [start, end]);

  const leadHours = vehicleDeliveryLeadHours(v);
  const windowError = useMemo(
    () => bookingWindowError(start, end) || pickupLeadTimeError(start, leadHours),
    [start, end, leadHours],
  );

  // Live availability validation — runs whenever dates or fetched ranges change.
  const bookedConflict = useMemo(() => {
    if (!start || !end) return false;
    return rangeOverlapsRanges(start, end, bookedRanges);
  }, [start, end, bookedRanges]);

  const pauseConflict = useMemo(() => {
    if (!start || !end) return false;
    return rangeHitsScheduledPause(start, end, listingPauses);
  }, [start, end, listingPauses]);

  const hostConflict = useMemo(() => {
    if (!start || !end) return false;
    return rangeHitsHostCalendar(start, end, calendarMode, calendarDays, listingPauses);
  }, [start, end, calendarMode, calendarDays, listingPauses]);

  const conflict = bookedConflict || hostConflict;

  const conflictMessage = pauseConflict
    ? formatBookingError("dates_in_scheduled_pause")
    : hostConflict
    ? (calendarMode === "define_rentable"
      ? formatBookingError("dates_not_rentable")
      : formatBookingError("dates_blocked_by_host"))
    : "Not available on these dates. Greyed-out days are booked, blocked by the host, or inside a scheduled pause.";

  const oneWayActive = oneWay && !!(dropoffLoc || "").trim();
  const quoteEnabled = !!(id && start && end && !windowError && !conflict);
  const { quote: tripQuote, loading: quoteLoading, quoteError, quoteValid } = useTripQuote(id, start, end, {
    enabled: quoteEnabled,
    pickupLocation: pickupLoc,
    dropoffLocation: dropoffLoc,
    dropoffPostal: dropoffPostal,
    oneWay: oneWayActive,
    redeemZct,
    applyWallet,
    couponCode: coupon?.code || "",
  });
  const oneWayDistanceError = quoteError && typeof quoteError === "string" && (
    quoteError.startsWith("one_way_distance_exceeded:")
    || quoteError.startsWith("one_way_address_unresolved:")
    || quoteError.startsWith("one_way_geocoding_unavailable:")
  )
    ? formatBookingError(quoteError)
    : null;
  const oneWayOutOfRange = typeof quoteError === "string"
    && quoteError.startsWith("one_way_distance_exceeded:");

  // Once the backend quote resolves, adopt its authoritative pickup address
  // (the public vehicle GET redacts location_address for privacy — KAN-106).
  useEffect(() => {
    if (tripQuote?.pickup_location) setPickupLoc(tripQuote.pickup_location);
  }, [tripQuote?.pickup_location]);

  // Auto-save the in-progress checkout so the user can resume from Home if they bail.
  useEffect(() => {
    if (!user || !id || !start || !end) return;
    if (conflict || windowError) return;  // don't save broken state
    saveDraft({ vehicle_id: id, start_date: start, end_date: end, redeem_zct: redeemZct, client_tz: clientTimeZone() });
  }, [user, id, start, end, redeemZct, conflict, windowError, saveDraft]);

  const breakdown = tripQuote?.price_breakdown;
  const quoteAmounts = bookingQuoteDisplay(tripQuote);
  const dayBreakdown = getDayBreakdown(tripQuote);
  const subtotal = quoteAmounts?.subtotal ?? 0;
  const avgDaily = tripQuote?.avg_daily_rate;
  const taxesAndFees = quoteAmounts?.taxesAndFees ?? 0;
  const taxAmount = quoteAmounts?.taxAmount ?? 0;
  const otherFees = quoteAmounts?.otherFees ?? 0;
  const oneWayFee = quoteAmounts?.oneWayFee ?? 0;
  const oneWayBaseFee = Number(v?.one_way_fee ?? 30);
  const oneWayDisplayFee = breakdown?.one_way_fee != null ? Number(breakdown.one_way_fee) : oneWayBaseFee;
  const afterHoursPickupFee = quoteAmounts?.afterHoursPickupFee ?? 0;
  const afterHoursReturnFee = quoteAmounts?.afterHoursReturnFee ?? 0;
  const opOpen = breakdown?.operating_open ?? v?.operating_open;
  const opClose = breakdown?.operating_close ?? v?.operating_close;
  const listingAhFee = breakdown?.listing_after_hours_fee ?? v?.after_hours_fee;
  const redeemRate = loyalty?.redeem_rate || 100;
  const maxRedeem = Math.min(
    loyalty?.loyalty?.zct_balance || 0,
    Math.floor((quoteAmounts?.preDiscountTotal || 0) * redeemRate * 0.5),
  );
  const discount = quoteAmounts?.zctDiscount ?? 0;
  const couponDiscount = quoteAmounts?.couponDiscount ?? 0;
  const walletCredit = quoteAmounts?.walletCredit ?? 0;
  const walletBalance = quoteAmounts?.walletBalance ?? 0;
  const walletEligible = quoteAmounts?.walletCreditEligible ?? false;
  const totalDiscount = discount + couponDiscount;
  const total = quoteAmounts?.total ?? 0;
  const depositAmount = quoteAmounts?.depositAmount ?? 0;
  const depositCopy = quoteAmounts?.depositCopy;
  const amountDue = quoteAmounts?.amountDue ?? (total + depositAmount);
  const currency = vehicleCurrency(v);
  const money = (amount) => formatMoney(amount, currency);

  const bookingPayload = () => ({
    vehicle_id: id,
    start_date: start,
    end_date: end,
    redeem_zct: redeemZct,
    apply_wallet: applyWallet,
    coupon_code: coupon?.code || null,
    pickup_location: pickupLoc.trim(),
    dropoff_location: oneWay ? dropoffLoc.trim() : pickupLoc.trim(),
    dropoff_postal: oneWay && dropoffPostal.trim() ? dropoffPostal.trim() : null,
    one_way: oneWay && !!dropoffLoc.trim(),
    add_esim: addEsim,
    client_tz: clientTimeZone(),
  });

  const isBookingValidationError = (err) => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    if (status === 403 && detail === "kyc_required") return true;
    if (status === 403 && detail === "profile_photo_required") return true;
    if (status === 409) return true;
    if (status === 400) return true;
    return false;
  };

  const completeBookingCreate = async () => {
    const res = await axios.post(`${API}/bookings`, bookingPayload(), { withCredentials: true });
    setConfirmed(res.data);
    if (res.data?.esim) setConfirmedEsim(res.data.esim);
    clearDraft();
    toast.success("Booking confirmed");
  };

  const submit = async () => {
    if (submitting) return;
    refreshMinPickup();
    const liveWindowError = bookingWindowError(start, end);
    if (!start || !end) { toast.error("Select pick-up and return"); return; }
    if (liveWindowError) { toast.error(liveWindowError); return; }
    if (conflict) { toast.error(conflictMessage); return; }
    if (!(pickupLoc || "").trim()) { toast.error("Enter a pick-up location"); return; }
    if (oneWay && !(dropoffLoc || "").trim()) { toast.error("Enter a drop-off location or switch to 'Same as pick-up'"); return; }
    if (oneWayDistanceError) { toast.error(oneWayDistanceError); return; }
    if (!canPayWithTripQuote({ quoteEnabled, quoteValid, loading: quoteLoading, quoteError })) {
      toast.error("Wait for a verified trip price before paying");
      return;
    }
    setSubmitting(true);
    try {
      const payload = bookingPayload();
      // KAN-226: fully covered by wallet/ZCT/coupon — confirm without Stripe.
      const prepaidZeroDue = amountDue === 0 && (walletCredit > 0 || discount > 0 || couponDiscount > 0);
      if (prepaidZeroDue) {
        await completeBookingCreate();
        return;
      }
      if (stripeConfigured && amountDue > 0 && amountDue < 0.5) {
        toast.error("Amount due is below the card minimum — apply more Z-Wallet credit or adjust the trip");
        return;
      }
      const tryStripe = stripeConfigured && amountDue >= 0.5;

      if (tryStripe) {
        try {
          const sr = await axios.post(`${API}/payments/stripe/checkout-session`, payload, { withCredentials: true });
          if (sr.data?.url) {
            window.location.assign(sr.data.url);
            return;
          }
        } catch (stripeErr) {
          if (isBookingValidationError(stripeErr)) throw stripeErr;
          toast.message("Stripe unavailable — completing with card on file flow");
        }
      }

      await completeBookingCreate();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail === "kyc_required") {
        setKycOpen(true);
      } else if (e.response?.status === 403 && detail === "profile_photo_required") {
        toast.error(formatBookingError(detail));
        navigate("/profile/settings");
      } else if (e.response?.status === 403 && detail === "renter_underage") {
        toast.error(formatBookingError(detail));
      } else if (e.response?.status === 403 && detail === "date_of_birth_required") {
        toast.error(formatBookingError(detail));
        navigate("/profile/settings");
      } else if (e.response?.status === 409 && detail === "active_booking_exists") {
        toast.error(formatBookingError(detail));
        try {
          const ar = await axios.get(`${API}/bookings/mine/active`, { withCredentials: true });
          setActiveBooking(ar.data || null);
        } catch (_) {}
      } else if (e.response?.status === 409) {
        toast.error("Just got booked by someone else — refreshing availability");
        try {
          const ar = await axios.get(`${API}/vehicles/${id}/availability`);
          setBookedRanges(ar.data?.booked_ranges || []);
          setCalendarMode(ar.data?.calendar_mode || "block_unavailable");
          setCalendarDays(ar.data?.calendar_days || ar.data?.blocked_days || []);
          setListingPauses(ar.data?.listing_pauses || []);
        } catch (_) {}
      } else if (
        e.response?.status === 400
        && typeof detail === "string"
        && (detail === "dates_blocked_by_host" || detail === "dates_not_rentable" || detail === "no_rentable_days_defined" || detail === "dates_in_scheduled_pause")
      ) {
        toast.error(formatBookingError(detail));
        try {
          const ar = await axios.get(`${API}/vehicles/${id}/availability`);
          setBookedRanges(ar.data?.booked_ranges || []);
          setCalendarMode(ar.data?.calendar_mode || "block_unavailable");
          setCalendarDays(ar.data?.calendar_days || ar.data?.blocked_days || []);
          setListingPauses(ar.data?.listing_pauses || []);
        } catch (_) {}
      } else if (e.response?.status === 400 && typeof detail === "string" && (detail.startsWith("pickup_lead_time:") || detail.startsWith("one_way_distance_exceeded:"))) {
        toast.error(formatBookingError(detail));
      } else {
        toast.error(formatBookingError(detail) || "Booking failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onKycApproved = async () => {
    setKycOpen(false);
    await checkAuth?.();
    toast.success("Identity verified — confirming your trip");
    submit();
  };

  if (!v && loadError) {
    return (
      <div className="max-w-[640px] mx-auto px-6 py-20 text-center" data-testid="bk-load-error">
        <h1 className="zc-headline text-3xl">Could not load checkout</h1>
        <p className="text-[var(--zc-text-2)] mt-3">{loadError}</p>
        <button
          type="button"
          onClick={() => setLoadAttempt((n) => n + 1)}
          className="mt-8 px-5 h-11 zc-btn-primary text-sm"
          data-testid="bk-load-retry"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!v) return <div className="max-w-[1400px] mx-auto px-6 py-20 text-[var(--zc-text-2)]" data-testid="bk-loading">Loading…</div>;

  if (confirmed) {
    return (
      <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-20" data-testid="bk-confirmed">
        <div className="zc-card p-6 sm:p-10 text-center">
          <div className="w-14 h-14 mx-auto grid place-items-center rounded-full" style={{ background: "rgba(22,163,74,0.12)" }}>
            <CheckCircle2 size={24} className="text-[var(--zc-success)]" />
          </div>
          <div className="eyebrow mt-6">Confirmed</div>
          <h1 className="zc-headline text-4xl mt-2">Your trip is booked.</h1>
          <p className="text-[var(--zc-text-2)] mt-3 max-w-md mx-auto">
            {v.year} {v.make} {v.model}
          </p>
          <BookingScheduleSummary booking={confirmed} testId="bk-confirmed-schedule" />
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-10 text-left">
            <div className="zc-card p-4">
              <div className="eyebrow mb-1">Booking ID</div>
              <div className="font-mono text-xs">{confirmed.booking_id}</div>
            </div>
            <div className="zc-card p-4">
              <div className="eyebrow mb-1">Total</div>
              <div className="zc-price text-2xl">{money(confirmed.total)}</div>
            </div>
            <div className="zc-card p-4">
              <div className="eyebrow mb-1">Status</div>
              <div className="text-sm flex items-center gap-2 mt-1"><span className={"dot " + bookingStatusDotClass(confirmed.status)} /> {formatBookingStatus(confirmed)}</div>
            </div>
            <div className="zc-card p-4">
              <div className="eyebrow mb-1">Booked on</div>
              <div className="text-xs text-[var(--zc-text-2)]">{confirmed.created_at ? formatLocalTimestamp(confirmed.created_at) : "—"}</div>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            <Link to="/dashboard/renter" className="px-5 h-11 grid place-items-center zc-btn-primary text-sm" data-testid="bk-go-trips">View my trips</Link>
            <Link to="/browse" className="px-5 h-11 grid place-items-center zc-btn-ghost text-sm" data-testid="bk-keep-browsing">Keep browsing</Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-6 text-left">
          <PolicyCard bookingId={confirmed.booking_id} insurance={confirmed.insurance} />
          <TelematicsPanel bookingId={confirmed.booking_id} isRenter status={confirmed.status} />
        </div>

        {/* Travel Pack eSIM — only rendered when one was issued for this booking. */}
        {confirmedEsim && (
          <div className="mt-6 text-left" data-testid="bk-esim-block">
            <div className="eyebrow mb-3">Travel Pack · eSIM</div>
            <EsimCard esim={confirmedEsim} onUpdate={async () => {
              try {
                const er = await axios.get(`${API}/esim/mine`, { withCredentials: true });
                const refreshed = pageItems(er.data).find((e) => e.activation_id === confirmedEsim.activation_id);
                if (refreshed) setConfirmedEsim(refreshed);
              } catch (_) {}
            }} />
          </div>
        )}
      </div>
    );
  }

  const quotePayable = canPayWithTripQuote({ quoteEnabled, quoteValid, loading: quoteLoading, quoteError });
  const confirmDisabled = submitting || days === 0 || !!windowError || conflict || !!activeBooking || !!oneWayDistanceError || !quotePayable;
  const confirmLabel = submitting
    ? "Confirming…"
    : quoteLoading
      ? "Updating trip price…"
      : quoteError && !oneWayDistanceError
        ? "Trip price unavailable"
    : oneWayDistanceError
      ? (oneWayOutOfRange ? "Drop-off too far from pick-up" : "Verify drop-off address")
      : activeBooking
        ? "Finish your open trip first"
        : conflict
          ? "Not available on these dates"
          : windowError
            ? "Adjust trip dates"
            : total === 0 && (walletCredit > 0 || discount > 0 || couponDiscount > 0)
              ? (walletCredit > 0 ? "Confirm with Z-Wallet" : "Confirm booking")
            : stripeConfigured && total >= 0.5
              ? `Pay ${money(total)} with Stripe`
              : `Confirm & pay ${money(total)}`;

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 pb-24 lg:pb-12 min-w-0" data-testid="bk-page">
      <div className="eyebrow mb-2">Checkout</div>
      <h1 className="zc-headline text-4xl md:text-5xl">Confirm your trip</h1>

      {activeBooking && (
        <div
          className="mt-6 p-4 rounded-lg flex items-start gap-3 text-sm border min-w-0"
          style={{ background: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.35)" }}
          data-testid="bk-active-trip-banner"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5 text-[var(--zc-warn)]" />
          <div className="min-w-0">
            <div className="font-semibold text-[var(--zc-warn)]">You already have an open trip</div>
            <p className="text-[var(--zc-text-2)] mt-1 text-xs">
              Booking {activeBooking.booking_id} is {formatBookingStatus(activeBooking)}. Finish or cancel it before booking another vehicle.
            </p>
            <Link to="/profile/bookings" className="text-xs font-semibold text-[var(--zc-accent)] mt-2 inline-block">View my bookings →</Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,420px] gap-10 mt-10 min-w-0">
        <div className="space-y-5 min-w-0">
          <div className="zc-card p-6">
            <div className="eyebrow mb-3">Trip</div>
            <div className="grid grid-cols-1 sm:grid-cols-[140px,1fr] gap-4 items-center">
              <ListingImage images={v?.images} alt="" className="w-full max-w-[140px] sm:max-w-none aspect-[4/3] object-cover rounded-lg" />
              <div className="min-w-0">
                <div className="zc-title text-xl">{v.year} {v.make} {v.model}</div>
                <div className="text-xs text-[var(--zc-text-3)] mt-1">{v.location_city}</div>
                <div className="text-sm text-[var(--zc-text-2)] mt-2" data-testid="bk-trip-dates">
                  {start && end ? formatTripRange(start, end) : "—"}
                </div>
              </div>
            </div>

            <div className="mt-5 pt-5 border-t border-[var(--zc-border)]">
              <div className="eyebrow mb-3">Pick-up</div>
              <SplitDateTime
                dateLabel="Pick-up date" timeLabel="Pick-up time"
                value={start || ""} onChange={onStartChange}
                minDate={minPickup}
                bookedRanges={bookedRanges}
                calendarMode={calendarMode}
                calendarDays={calendarDays}
                listingPauses={listingPauses}
                operatingOpen={opOpen}
                operatingClose={opClose}
                testidPrefix="bk-pickup"
                priceVehicleId={id}
                helperText="Greyed days are booked, blocked by the host, or inside a scheduled pause. Night / outside-hours slots stay selectable (after-hours fee)."
              />
            </div>

            <div className="mt-5 pt-5 border-t border-[var(--zc-border)]">
              <div className="eyebrow mb-3">Return</div>
              <SplitDateTime
                dateLabel="Return date" timeLabel="Return time"
                value={end || ""} onChange={(v) => setDate("end", v)}
                minDate={start || minPickup}
                bookedRanges={bookedRanges}
                calendarMode={calendarMode}
                calendarDays={calendarDays}
                listingPauses={listingPauses}
                operatingOpen={opOpen}
                operatingClose={opClose}
                testidPrefix="bk-dropoff"
                priceVehicleId={id}
                helperText="Return time is independent of pick-up — adjust if you need a different drop-off hour."
              />
              {bookingTimeZoneLabel(tripQuote) && (
                <p className="zc-hint mt-3" data-testid="bk-booking-timezone">
                  Times shown in {bookingTimeZoneLabel(tripQuote)}
                </p>
              )}
            </div>

            {windowError && (
              <div className="mt-4 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "var(--zc-error, #dc2626)" }} data-testid="bk-window-error">
                <AlertTriangle size={14} /> {windowError}
              </div>
            )}
            {conflict && (
              <div className="mt-4 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "var(--zc-error, #dc2626)" }} data-testid="bk-conflict-banner">
                <AlertTriangle size={14} /> {conflictMessage}
              </div>
            )}
            <LateReturnWarning pickupAt={start} returnAt={end} operatingOpen={opOpen} operatingClose={opClose} afterHoursFeeAmount={listingAhFee} />
          </div>

          <div className="zc-card p-6" data-testid="bk-locations-card">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={14} className="text-[var(--zc-accent)]" />
              <div className="eyebrow">Locations</div>
            </div>

            <label className="text-xs text-[var(--zc-text-3)] block mb-1.5">Pick-up location</label>
            <input
              value={pickupLoc}
              readOnly
              disabled
              placeholder="123 Main St, Toronto"
              className="zc-input h-11 px-3 w-full text-sm opacity-80 cursor-not-allowed"
              data-testid="bk-pickup-location"
            />
            <div className="zc-hint mt-1.5">Set by the host — pick-up is at the vehicle&apos;s listed address.</div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 p-1 rounded-xl bg-[var(--zc-bg-2)] border border-[var(--zc-border)]" data-testid="bk-dropoff-toggle">
              <button
                type="button"
                onClick={() => setOneWay(false)}
                className={"min-h-10 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 flex-wrap " + (!oneWay ? "bg-white text-[var(--zc-text)] shadow-sm" : "text-[var(--zc-text-3)]")}
                data-testid="bk-dropoff-same"
              >
                <MapPin size={12} className="shrink-0" /> Same as pick-up
              </button>
              <button
                type="button"
                onClick={() => v?.allows_one_way && setOneWay(true)}
                disabled={!v?.allows_one_way}
                className={"min-h-10 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 flex-wrap " + (oneWay ? "bg-white text-[var(--zc-text)] shadow-sm" : "text-[var(--zc-text-3)]") + (!v?.allows_one_way ? " opacity-50 cursor-not-allowed" : "")}
                data-testid="bk-dropoff-different"
              >
                <ArrowLeftRight size={12} className="shrink-0" /> Different return location {v?.allows_one_way && (
                  <span className="text-2xs font-mono px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "var(--zc-accent-soft)", color: "var(--zc-accent)" }}>
                    from +${formatNumber(oneWayBaseFee, 0)}
                  </span>
                )}
              </button>
            </div>
            {!v?.allows_one_way && (
              <div className="zc-hint mt-1.5" data-testid="bk-oneway-disabled-hint">
                This host does not allow a different return location.
              </div>
            )}

            {oneWay && (
              <div className="mt-3" data-testid="bk-dropoff-section">
                <label className="text-xs text-[var(--zc-text-3)] block mb-1.5">Return location</label>
                <input
                  value={dropoffLoc}
                  onChange={(e) => setDropoffLoc(e.target.value)}
                  placeholder="Full street address for return"
                  className="zc-input h-11 px-3 w-full text-sm"
                  data-testid="bk-dropoff-location"
                />
                <label className="text-xs text-[var(--zc-text-3)] block mb-1.5 mt-3">Postal / ZIP code</label>
                <input
                  value={dropoffPostal}
                  onChange={(e) => setDropoffPostal(e.target.value)}
                  placeholder="e.g. M5V 2T6"
                  className="zc-input h-11 px-3 w-full text-sm max-w-xs"
                  data-testid="bk-dropoff-postal"
                />
                {(dropoffLoc || "").trim() && (
                  <div className="zc-hint mt-1.5">
                    Drop-off must be within {ONE_WAY_MAX_KM} km of pick-up
                    {tripQuote?.one_way_distance_km != null && !oneWayDistanceError && (
                      <> · <b className="text-[var(--zc-text)]">{tripQuote.one_way_distance_km} km</b></>
                    )}.
                  </div>
                )}
                <div className="zc-hint mt-1.5">
                  {oneWayActive && tripQuote?.one_way_distance_km != null && !oneWayDistanceError ? (
                    <>One-way fee: <b className="text-[var(--zc-accent)]">{money(oneWayDisplayFee)}</b> (base + distance)</>
                  ) : (
                    <>A <b className="text-[var(--zc-accent)]">{money(oneWayBaseFee)}</b> base one-way fee applies; distance may increase the total.</>
                  )}
                </div>
                {oneWayDistanceError && (
                  <div className="mt-3 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "var(--zc-error, #dc2626)" }} data-testid="bk-oneway-distance-error">
                    <AlertTriangle size={14} /> {oneWayDistanceError}
                  </div>
                )}
              </div>
            )}
          </div>

          {airaloReady === false && (
            <p className="zc-hint mb-2">Travel Pack is temporarily unavailable.</p>
          )}
          <TravelPackToggle
            checked={addEsim}
            onToggle={setAddEsim}
            testid="bk-travel-pack"
            disabled={airaloReady === false}
          />

          <div className="zc-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard size={14} className="text-[var(--zc-accent)]" />
              <div className="eyebrow">Payment</div>
            </div>
            {!quotePayable ? (
              <p className="text-sm text-[var(--zc-text-2)]" data-testid="bk-payment-quote-pending">
                Payment is disabled until the current trip price is verified.
              </p>
            ) : total === 0 && (walletCredit > 0 || discount > 0 || couponDiscount > 0) ? (
              <>
                <p className="text-sm text-[var(--zc-text-2)]" data-testid="bk-payment-prepaid">
                  {walletCredit > 0
                    ? "This trip is fully covered by Z-Wallet — no card charge."
                    : "This trip is fully covered — no card charge."}
                </p>
                <div className="zc-hint mt-3 flex items-center gap-1.5">
                  <Lock size={11} /> Host payout is funded from your credit / trip total.
                </div>
              </>
            ) : stripeConfigured && total >= 0.5 ? (
              <>
                <p className="text-sm text-[var(--zc-text-2)]" data-testid="bk-payment-stripe">
                  You&apos;ll be redirected to <b>Stripe Checkout</b> to pay securely with your card.
                </p>
                <div className="zc-hint mt-3 flex items-center gap-1.5">
                  <Lock size={11} /> Funds held in escrow until trip completes.
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-[var(--zc-text-2)]" data-testid="bk-payment-card-fallback">
                  Card payment will be confirmed when you continue. Enter your card details on the next step if prompted.
                </p>
                <div className="zc-hint mt-3 flex items-center gap-1.5">
                  <Lock size={11} /> Funds held in escrow until trip completes.
                </div>
              </>
            )}
          </div>

          <div className="zc-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-[var(--zc-accent)]" />
              <div className="eyebrow">Redeem ZCT loyalty</div>
            </div>
            <div className="text-sm text-[var(--zc-text-2)]">Balance: <span className="text-[var(--zc-text)] font-semibold">{loyalty?.loyalty?.zct_balance || 0} ZCT</span> · 100 ZCT = $1 · max 50% off</div>
            <input type="range" min={0} max={maxRedeem} step={50} value={redeemZct} onChange={e => setRedeemZct(parseInt(e.target.value || "0"))}
              className="w-full mt-3 accent-[var(--zc-accent)]" data-testid="bk-zct-slider" />
            <div className="flex justify-between text-xs mt-1">
              <span className="text-[var(--zc-text-3)]">
                {quoteValid ? quoteAmounts?.redeemedZct : redeemZct} ZCT {quoteValid ? "applied" : "selected"}
              </span>
              <span className="text-[var(--zc-text)] font-semibold">−{money(discount)}</span>
            </div>
            {maxRedeem === 0 && <div className="zc-hint mt-2">No ZCT available to redeem.</div>}
          </div>

          <div className="zc-card p-6" data-testid="bk-wallet-card">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard size={14} className="text-[var(--zc-accent)]" />
              <div className="eyebrow">Z-Wallet credit</div>
            </div>
            <div className="text-sm text-[var(--zc-text-2)]">
              Balance: <span className="text-[var(--zc-text)] font-semibold">{money(walletBalance)}</span>
              <span className="block zc-hint mt-1">Applied at checkout as credit — not a payment method. Hosts still receive full trip escrow.</span>
            </div>
            {walletEligible && walletBalance > 0 ? (
              <label className="mt-3 flex items-center gap-3 cursor-pointer" data-testid="bk-wallet-toggle">
                <input
                  type="checkbox"
                  checked={applyWallet}
                  onChange={(e) => setApplyWallet(e.target.checked)}
                  className="h-4 w-4 accent-[var(--zc-accent)]"
                />
                <span className="text-sm text-[var(--zc-text)] font-semibold">
                  Apply Z-Wallet ({money(walletBalance)})
                </span>
              </label>
            ) : (
              <div className="zc-hint mt-2" data-testid="bk-wallet-ineligible">
                {quoteAmounts?.walletCreditIneligibleReason
                  ? formatBookingError(quoteAmounts.walletCreditIneligibleReason)
                  : "No Z-Wallet balance available to apply."}
              </div>
            )}
            {applyWallet && walletCredit > 0 && (
              <div className="flex justify-between text-xs mt-2">
                <span className="text-[var(--zc-text-3)]">Credit applied</span>
                <span className="text-[var(--zc-success)] font-semibold">−{money(walletCredit)}</span>
              </div>
            )}
          </div>

          <div className="zc-card p-6" data-testid="bk-coupon-card">
            <div className="flex items-center gap-2 mb-3">
              <Tag size={14} className="text-[var(--zc-accent)]" />
              <div className="eyebrow">Promo code</div>
            </div>
            {coupon ? (
              <div
                className="px-4 py-3 rounded-lg flex items-center justify-between gap-3"
                style={{ background: "var(--zc-accent-soft)", border: "1px solid rgba(220,38,38,0.25)" }}
                data-testid="bk-coupon-applied"
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm font-bold text-[var(--zc-accent)]">{coupon.code}</div>
                  <div className="zc-hint text-[var(--zc-text-2)] mt-0.5">
                    {quoteValid ? `−${money(couponDiscount)} applied` : "Verifying discount…"}
                  </div>
                </div>
                <button onClick={removeCoupon} className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/60 transition" aria-label="Remove" data-testid="bk-coupon-remove">
                  <X size={14} className="text-[var(--zc-text-2)]" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                    placeholder="WELCOMEBACK10"
                    className="zc-input h-10 flex-1 px-3 text-sm font-mono uppercase"
                    data-testid="bk-coupon-input"
                  />
                  <button
                    onClick={() => applyCoupon()}
                    disabled={couponBusy || !couponInput.trim()}
                    className="px-4 h-10 zc-btn-primary text-xs disabled:opacity-60"
                    data-testid="bk-coupon-apply"
                  >
                    {couponBusy ? "…" : "Apply"}
                  </button>
                </div>
                {activeCoupons.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="bk-coupon-suggestions">
                    {activeCoupons.slice(0, 3).map((c) => (
                      <button
                        key={c.code}
                        onClick={() => applyCoupon(c.code)}
                        className="text-caption px-2.5 h-7 rounded-full font-mono font-bold transition"
                        style={{ background: "var(--zc-accent-soft)", color: "var(--zc-accent)" }}
                        data-testid={`bk-coupon-suggest-${c.code}`}
                      >
                        Tap to apply · {c.code} · −${c.amount_usd}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <aside className="zc-card p-6 h-fit lg:sticky lg:top-24 min-w-0">
          <div className="eyebrow mb-3">Summary</div>
          {(pickupLoc || "").trim() && (
            <div className="text-xs text-[var(--zc-text-2)] mb-4 space-y-1.5 pb-4 border-b border-[var(--zc-border)] min-w-0" data-testid="bk-summary-locations">
              <div className="flex gap-2 min-w-0">
                <MapPin size={12} className="text-[var(--zc-accent)] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="eyebrow">Pick-up</div>
                  <div className="text-[var(--zc-text)] break-words">{pickupLoc}</div>
                </div>
              </div>
              {oneWayActive && (dropoffLoc || "").trim() && (
                <div className="flex gap-2 min-w-0">
                  <ArrowLeftRight size={12} className="text-[var(--zc-accent)] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="eyebrow">Drop-off</div>
                      {tripQuote && !oneWayDistanceError && tripQuote.one_way_distance_km != null && (
                        <span className="text-2xs font-semibold px-2 py-0.5 rounded-full shrink-0" style={{ background: "rgba(22,163,74,0.12)", color: "var(--zc-success)" }} data-testid="bk-oneway-zone-badge">
                          {tripQuote.one_way_distance_km} km · within zone
                        </span>
                      )}
                    </div>
                    <div className="text-[var(--zc-text)] break-words">{dropoffLoc}</div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="space-y-2 text-sm">
            <TripChargesGroup
              money={money}
              days={days}
              subtotal={subtotal}
              avgDaily={avgDaily}
              dayBreakdown={dayBreakdown}
              testid="bk-summary-charges"
            />
            {oneWayFee > 0 && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-oneway">
                <span className="text-[var(--zc-text-2)] min-w-0">One-way drop-off fee</span>
                <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(oneWayFee)}</span>
              </div>
            )}
            {afterHoursPickupFee > 0 && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-after-hours-pickup">
                <span className="text-[var(--zc-text-2)] min-w-0">
                  After-hours pick-up{opOpen && opClose ? ` (outside ${opOpen}–${opClose})` : " (10pm–6am)"}
                </span>
                <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(afterHoursPickupFee)}</span>
              </div>
            )}
            {afterHoursReturnFee > 0 && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-after-hours">
                <span className="text-[var(--zc-text-2)] min-w-0">
                  After-hours return{opOpen && opClose ? ` (outside ${opOpen}–${opClose})` : " (10pm–6am)"}
                </span>
                <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(afterHoursReturnFee)}</span>
              </div>
            )}
            {taxAmount > 0 && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-tax">
                <span className="text-[var(--zc-text-2)] min-w-0">
                  Tax{breakdown?.tax_rate_percent != null ? ` (${breakdown.tax_rate_percent}%)` : ""}
                  {breakdown?.tax_note && (
                    <span className="block zc-meta font-normal mt-0.5 text-[var(--zc-text-3)]">{breakdown.tax_note}</span>
                  )}
                </span>
                <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(taxAmount)}</span>
              </div>
            )}
            {otherFees > 0 && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-other-fees">
                <span className="text-[var(--zc-text-2)] min-w-0">
                  Service &amp; other fees
                  <span className="block zc-meta font-normal mt-0.5">Booking support &amp; platform services</span>
                </span>
                <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(otherFees)}</span>
              </div>
            )}
            {taxesAndFees > 0 && taxAmount === 0 && otherFees === 0 && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-taxes">
                <span className="text-[var(--zc-text-2)] min-w-0">
                  Taxes &amp; fees
                </span>
                <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(taxesAndFees)}</span>
              </div>
            )}
            {totalDiscount > 0 && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-discount">
                <span className="text-[var(--zc-success)] min-w-0">
                  Discount
                  <span className="block zc-meta font-normal mt-0.5 text-[var(--zc-text-3)]">
                    {[discount > 0 && "ZCT loyalty", couponDiscount > 0 && `Coupon · ${coupon.code}`].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <span className="text-[var(--zc-success)] font-semibold shrink-0">−{money(totalDiscount)}</span>
              </div>
            )}
            {walletCredit > 0 && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-wallet">
                <span className="text-[var(--zc-success)] min-w-0">Z-Wallet credit</span>
                <span className="text-[var(--zc-success)] font-semibold shrink-0">−{money(walletCredit)}</span>
              </div>
            )}
            {depositAmount > 0 && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-deposit">
                <span className="text-[var(--zc-text-2)] min-w-0">
                  Security deposit (refundable)
                  {depositCopy && (
                    <span className="block zc-meta font-normal mt-0.5 text-[var(--zc-text-3)]">
                      {depositCopy}
                    </span>
                  )}
                </span>
                <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(depositAmount)}</span>
              </div>
            )}
            {tripQuote?.insurance?.included && (
              <div className="flex justify-between gap-3" data-testid="bk-summary-insurance">
                <span className="text-[var(--zc-text-2)] min-w-0">
                  {tripQuote.insurance.label || "Insurance coverage included with your rental"}
                  <span className="block zc-meta font-normal mt-0.5">
                    <Link
                      to={tripQuote.insurance.coverage_details_path || "/coverage"}
                      className="text-[var(--zc-accent)] hover:underline"
                      data-testid="bk-summary-insurance-details"
                    >
                      Coverage details
                    </Link>
                  </span>
                </span>
                <span className="text-[var(--zc-success)] font-semibold shrink-0">Included</span>
              </div>
            )}
            {quoteLoading && (
              <div className="text-xs text-[var(--zc-text-2)] py-2" data-testid="bk-summary-quote-loading">
                Updating taxes, fees, and discounts…
              </div>
            )}
            {quoteError && !oneWayDistanceError && (
              <div className="text-xs text-[var(--zc-danger)] py-2" data-testid="bk-summary-quote-error">
                We couldn&apos;t verify this trip price. Adjust the trip or try again before paying.
              </div>
            )}
            {depositAmount > 0 && (
              <div className="flex justify-between gap-3 pt-2" data-testid="bk-summary-trip-total">
                <span className="text-[var(--zc-text-2)] min-w-0">Trip total</span>
                <span className="text-[var(--zc-text)] font-semibold shrink-0">{quotePayable ? money(total) : "—"}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline gap-3 pt-3 border-t border-[var(--zc-border)] mt-3">
              <span className="font-semibold shrink-0">{depositAmount > 0 ? "Amount due" : "Total"}</span>
              <span className="zc-price text-2xl sm:text-3xl shrink-0" data-testid="bk-summary-amount-due">
                {quotePayable ? money(amountDue) : "—"}
              </span>
            </div>
            {(() => {
              const distanceLine = formatIncludedDistanceLine({
                includedKmPerDay: tripQuote?.included_km_per_day ?? v?.included_km_per_day,
                includedKmTotal: tripQuote?.included_km_total,
                days: tripQuote?.days ?? days,
                overageFeePerKm: tripQuote?.overage_fee_per_km ?? v?.overage_fee_per_km,
              });
              if (!distanceLine) return null;
              return (
                <div className="text-xs text-[var(--zc-text-2)] pt-3 mt-1" data-testid="bk-summary-included-km">
                  {distanceLine}
                </div>
              );
            })()}
          </div>
          <div className="mt-5 pt-4 border-t border-[var(--zc-border)]" data-testid="bk-summary-road-rules">
            <div className="eyebrow mb-2">Road rules</div>
            <ul className="space-y-1.5 text-xs text-[var(--zc-text-2)]">
              {ROAD_RULES.map((rule) => (
                <li key={rule.id} className="flex gap-2" data-testid={`bk-road-rule-${rule.id}`}>
                  <span className="text-[var(--zc-accent)] shrink-0" aria-hidden>•</span>
                  <span>{formatRoadRuleLine(rule)}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/terms#road-rules"
              className="inline-block mt-2 text-xs text-[var(--zc-accent)] font-semibold underline-offset-2 hover:underline"
              data-testid="bk-road-rules-terms-link"
            >
              Full road rules in Terms
            </Link>
          </div>
          <button onClick={submit} disabled={confirmDisabled} className="w-full min-h-12 py-2 px-3 zc-btn-primary text-xs sm:text-sm mt-6 disabled:opacity-60" data-testid="bk-confirm-cta">
            {confirmLabel}
          </button>
          <div className="zc-hint mt-3">
            By continuing you accept{" "}
            <Link to="/terms" className="text-[var(--zc-accent)] font-semibold underline-offset-2 hover:underline">trip terms</Link>
            {" "}and our{" "}
            <Link to="/cancellation" className="text-[var(--zc-accent)] font-semibold underline-offset-2 hover:underline">cancellation policy</Link>.
          </div>
        </aside>
      </div>

      <div
        className="fixed bottom-0 inset-x-0 z-40 border-t border-[var(--zc-border)] bg-[var(--zc-bg)] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden"
        data-testid="bk-mobile-sticky-bar"
      >
        <div className="flex items-center gap-3 max-w-[1100px] mx-auto">
          <div className="min-w-0 flex-1">
            <div className="eyebrow">Total</div>
            <div className="zc-price text-xl">{quotePayable ? money(total) : "—"}</div>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={confirmDisabled}
            className="shrink-0 min-h-11 px-5 zc-btn-primary text-xs sm:text-sm disabled:opacity-60"
            data-testid="bk-mobile-confirm-cta"
          >
            {confirmLabel}
          </button>
        </div>
      </div>

      <KycModal open={kycOpen} onClose={() => setKycOpen(false)} onApproved={onKycApproved} />
    </div>
  );
}
